import React, { useState, useEffect } from 'react';

interface ToolCall {
  tool_name: string;
  display_name: string;
  status: 'in_progress' | 'completed';
}

interface ThoughtBubbleProps {
  isActive: boolean;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  toolCalls?: ToolCall[];
}

const ChevronIcon: React.FC<{ isCollapsed: boolean }> = ({ isCollapsed }) => (
  <svg
    className={`w-4 h-4 text-mobile-gray-400 transition-transform duration-200 ${
      isCollapsed ? '' : 'rotate-180'
    }`}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
  </svg>
);

const SpinnerIcon: React.FC = () => (
  <svg
    className="w-4 h-4 text-mobile-gray-400 animate-spin"
    fill="none"
    viewBox="0 0 24 24"
  >
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    />
  </svg>
);

const CheckIcon: React.FC = () => (
  <svg
    className="w-4 h-4 text-green-500"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);

const ToolCallItem: React.FC<{ toolCall: ToolCall }> = ({ toolCall }) => (
  <div className="flex items-center gap-3 py-2 px-3 bg-white rounded-lg border border-mobile-gray-200 shadow-sm">
    {toolCall.status === 'in_progress' ? <SpinnerIcon /> : <CheckIcon />}
    <span className="text-mobile-gray-700 text-sm font-medium">{toolCall.display_name}</span>
  </div>
);

const ThoughtBubble: React.FC<ThoughtBubbleProps> = ({
  isActive,
  isCollapsed,
  onToggleCollapse,
  toolCalls = [],
}) => {
  const [dots, setDots] = useState('');

  useEffect(() => {
    if (!isActive) return;

    const interval = setInterval(() => {
      setDots((prev) => (prev.length >= 3 ? '' : prev + '.'));
    }, 400);

    return () => clearInterval(interval);
  }, [isActive]);

  return (
    <div className="flex items-start flex-col self-start max-w-[90%] break-words text-sm m-2">
      <div className="flex items-end">
        <img src="/plumLogo.svg" width={32} height={32} alt="Plum" />
        <div className="ml-3 bg-mobile-gray-50 rounded-tl-xl rounded-tr-xl rounded-br-xl overflow-hidden shadow-sm">
          <button
            type="button"
            className="w-full px-4 py-2 pr-10 text-mobile-gray-500 cursor-pointer hover:bg-mobile-gray-100 transition-colors relative"
            onClick={onToggleCollapse}
            aria-expanded={!isCollapsed}
            aria-label="Toggle thought details"
          >
            <div className="text-left">
              <span className="text-mobile-gray-600 font-medium">
                {isActive ? (
                  <span className="inline-flex items-center">
                    Thinking
                    <span className="w-6 text-left">{dots}</span>
                  </span>
                ) : (
                  'Thought'
                )}
              </span>
            </div>
            <div className="absolute top-2 right-3">
              <ChevronIcon isCollapsed={isCollapsed} />
            </div>
          </button>
          
          {!isCollapsed && toolCalls.length > 0 && (
            <div className="px-4 pb-4 border-t border-mobile-gray-200">
              <div className="space-y-2 mt-3">
                {toolCalls.map((toolCall, index) => (
                  <ToolCallItem key={`${toolCall.tool_name}-${index}`} toolCall={toolCall} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ThoughtBubble;
